import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Toolbar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DefaultLayoutBody from 'components/layout/DefaultLayoutBody';
import AppBar from 'components/shared/AppBar';
import GlobalBannerBlock from 'components/shared/blocks/GlobalBannerBlock';
import PushPermissionBanner from 'components/shared/blocks/PushPermissionBanner';
import DesktopDrawer from 'components/shared/drawers/DesktopDrawer';
import MobileTabBar from 'components/shared/navigation/MobileTabBar';
import PlayWorkoutMiniBar from 'components/play/PlayWorkoutMiniBar';
import SubscriptionGraceGate from 'components/client-payment/SubscriptionGraceGate';
import { userMembershipFields } from 'common/constants/query-fields/user';
import { fullDrawerWidth, miniDrawerWidth, sidebarGutter } from 'common/constants/general';
import { roles } from 'common/constants/options';
import StyledDefaultLayoutBannerContainer from 'common/theme/layouts/default/StyledDefaultLayoutBannerContainer';
import StyledDefaultLayoutContainer from 'common/theme/layouts/default/StyledDefaultLayoutContainer';
import StyledDefaultLayoutContent from 'common/theme/layouts/default/StyledDefaultLayoutContent';
import StyledDefaultLayoutMain from 'common/theme/layouts/default/StyledDefaultLayoutMain';
import StyledDefaultLayoutSidebar from 'common/theme/layouts/default/StyledDefaultLayoutSidebar';
import AppbarContextProvider from 'context/AppbarContextProvider';
import authSlice from 'store/modules/auth';

function DefaultLayout({ children }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const role = useSelector((state) => state.auth.user?.role);
  const isClientUser = role === roles.client;

  const miniDrawer = useSelector((state) => state.settings.config.miniDrawer);
  const drawerWidth = miniDrawer ? miniDrawerWidth : fullDrawerWidth;
  // El sidebar ahora FLOTA con margen: el layout reserva panel + gutter a ambos lados.
  const reservedWidth = drawerWidth + sidebarGutter * 2;

  useEffect(() => {
    if (isClientUser) {
      dispatch(authSlice.actions.me(userMembershipFields));
    }
  }, [dispatch, isClientUser]);

  return (
    <StyledDefaultLayoutContainer>
        {/* Bajo lg NO se monta el drawer desktop (antes solo se ocultaba por CSS): sus anclas
            data-tour duplicarían las del drawer mobile y el tour resaltaría el elemento oculto. */}
        {isDesktop && (
          <StyledDefaultLayoutSidebar component="nav" drawerWidth={reservedWidth}>
            <DesktopDrawer drawerWidth={drawerWidth} />
          </StyledDefaultLayoutSidebar>
        )}
        {/* El drawer MÓVIL y su contexto ya NO viven aquí: se montan una sola vez en el router,
            por encima de <Routes>. Este layout se construye dentro del `element` de CADA ruta, así
            que React Router lo desmontaba y volvía a montarlo en cada navegación. Cuando la página
            destino ya estaba precargada, ese desmontaje ocurría a media animación de cierre: el
            Modal de MUI moría sin ejecutar su limpieza y dejaba el bloqueo de scroll y el velo
            puestos, con el panel congelado hasta recargar. Con páginas sin precargar, Suspense
            retrasaba el desmontaje lo justo para que la animación terminara, y por eso el fallo
            solo aparecía en las pantallas ya visitadas. */}
        <StyledDefaultLayoutMain component="main" drawerWidth={reservedWidth}>
          <AppbarContextProvider>
            <AppBar />
            <div>
              <Toolbar />
              <StyledDefaultLayoutContent spacing={{ xs: 3, md: 4 }}>
                <StyledDefaultLayoutBannerContainer spacing={2}>
                  <GlobalBannerBlock />
                  <PushPermissionBanner />
                </StyledDefaultLayoutBannerContainer>
                <DefaultLayoutBody>{children}</DefaultLayoutBody>
                {/* Colchón del alto de la barra inferior (fija) para que el último elemento de
                    cada pantalla no quede debajo de ella y sin poder tocarse. */}
                {!/OtrixApp/.test(window.navigator.userAgent) && (
                  <Box
                    aria-hidden
                    sx={{
                      display: { xs: 'block', lg: 'none' },
                      height: 'calc(56px + env(safe-area-inset-bottom, 0px))'
                    }}
                  />
                )}
              </StyledDefaultLayoutContent>
            </div>
          </AppbarContextProvider>
          {/* Notifier vive UNA sola vez en router/index.js (montarlo aquí duplicaba toasts). */}
          {isClientUser && <SubscriptionGraceGate />}
          {isClientUser && <PlayWorkoutMiniBar />}
          {/* Se monta para TODOS los roles: quitamos la hamburguesa de la cabecera, así que la
              foto de esta barra es la única puerta al menú en móvil. Quién ve barra y con qué
              destinos lo decide el propio componente (el gestor de empresa queda fuera y conserva
              su hamburguesa). Limitarlo aquí a cliente dejaba a entrenador y admin SIN navegación. */}
          <MobileTabBar />
        </StyledDefaultLayoutMain>
    </StyledDefaultLayoutContainer>
  );
}

DefaultLayout.propTypes = {
  children: PropTypes.element.isRequired
};

export default DefaultLayout;

